// Audit Log panel — Calyvra Administrators only
import { useState, useEffect, useCallback } from 'react';
import { ScrollText, Download, Search, ChevronLeft, ChevronRight, Loader2, ShieldAlert, Filter } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import portalApi from '../lib/api';
import { toast } from 'sonner';

const SECURITY_ACTIONS = new Set([
  'successful_login', 'failed_login', 'account_lockout', 'mfa_lockout', 'mfa_enrollment',
  'mfa_reset', 'mfa_skipped_trusted_device', 'passkey_registered', 'passkey_removed',
  'passkey_login', 'ssn_revealed', 'cases_exported', 'cases.exported', 'audit_log.exported',
]);

const DANGER_ACTIONS = new Set(['failed_login', 'account_lockout', 'mfa_lockout', 'ssn_revealed', 'cases_exported']);

function actionTone(action) {
  if (DANGER_ACTIONS.has(action)) return 'bg-red-50 text-red-700 border-red-200';
  if (action?.startsWith('passkey') || action === 'successful_login' || action === 'mfa_enrollment') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
}

function fmt(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

export default function AuditLogPage() {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const [emailInput, setEmailInput] = useState('');
  const [filters, setFilters] = useState({ user_email: '', action: 'all', start: '', end: '', category: 'security' });

  const limit = 50;

  const queryParams = useCallback((extra = {}) => ({
    user_email: filters.user_email || undefined,
    action: filters.action !== 'all' ? filters.action : undefined,
    start: filters.start || undefined,
    end: filters.end || undefined,
    category: filters.category === 'security' ? 'security' : undefined,
    ...extra,
  }), [filters]);

  const load = useCallback(async (targetPage = page) => {
    setLoading(true);
    try {
      const data = await portalApi.getAuditLogs(queryParams({ page: targetPage, limit }));
      setLogs(data.logs || []);
      setActions(data.actions || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(data.page || 1);
    } catch (err) {
      toast.error(err.message || 'Could not load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, queryParams]);

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [filters]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await portalApi.exportAuditLogs(queryParams());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Audit log exported');
    } catch (err) {
      toast.error(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const applyEmail = (e) => {
    e.preventDefault();
    setFilters((f) => ({ ...f, user_email: emailInput.trim() }));
  };

  const actionOptions = filters.category === 'security'
    ? actions.filter((a) => SECURITY_ACTIONS.has(a.value))
    : actions;

  return (
    <div data-testid="audit-log-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ScrollText className="h-6 w-6 text-sky-600" /> Audit Log
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Security and access events across the portal. {total.toLocaleString()} record{total === 1 ? '' : 's'}.
          </p>
        </div>
        <Button onClick={handleExport} disabled={exporting || total === 0} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="export-audit-btn">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm mb-5">
        <div className="flex items-center gap-2 text-slate-500 text-xs font-medium uppercase tracking-wide mb-3">
          <Filter className="h-3.5 w-3.5" /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <form onSubmit={applyEmail} className="lg:col-span-2">
            <Label className="text-xs text-slate-500">User email</Label>
            <div className="relative mt-1">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="Search by email…"
                className="pl-9"
                data-testid="audit-email-filter"
              />
            </div>
          </form>

          <div>
            <Label className="text-xs text-slate-500">Scope</Label>
            <Select value={filters.category} onValueChange={(v) => setFilters((f) => ({ ...f, category: v, action: 'all' }))}>
              <SelectTrigger className="mt-1" data-testid="audit-scope-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="security">Security events</SelectItem>
                <SelectItem value="all">All events</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-slate-500">Action</Label>
            <Select value={filters.action} onValueChange={(v) => setFilters((f) => ({ ...f, action: v }))}>
              <SelectTrigger className="mt-1" data-testid="audit-action-filter"><SelectValue placeholder="All actions" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All actions</SelectItem>
                {actionOptions.map((a) => (
                  <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-slate-500">From</Label>
              <Input type="date" value={filters.start} onChange={(e) => setFilters((f) => ({ ...f, start: e.target.value }))} className="mt-1" data-testid="audit-start-filter" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">To</Label>
              <Input type="date" value={filters.end} onChange={(e) => setFilters((f) => ({ ...f, end: e.target.value }))} className="mt-1" data-testid="audit-end-filter" />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16" data-testid="audit-empty">
            <ShieldAlert className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No events match these filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="px-4 py-3 font-medium">When</th>
                  <th className="px-4 py-3 font-medium">Action</th>
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Scope</th>
                  <th className="px-4 py-3 font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid="audit-row">
                    <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmt(r.timestamp)}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`font-medium ${actionTone(r.action)}`}>{r.action_label || r.action}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-900">{r.user_name || '—'}</div>
                      <div className="text-xs text-slate-400">{r.user_email || ''}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs">{r.case_id}</td>
                    <td className="px-4 py-3 text-slate-500 max-w-xs truncate">
                      {r.details && Object.keys(r.details).length
                        ? Object.entries(r.details).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <span className="text-xs text-slate-500">Page {page} of {pages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => load(page - 1)} data-testid="audit-prev-page">
                <ChevronLeft className="h-4 w-4" /> Prev
              </Button>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => load(page + 1)} data-testid="audit-next-page">
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
